import * as React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Button as MUIButton,
  Checkbox,
} from "@mui/material";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { centered_flex_box, main_button } from "../../app/components/Styles";
import { FoKpis, formatEgp } from "./financeUi";
import { getVendorFinanceDetails } from "../../app/store/actions/whFinanceActions";
import {
  Store,
  ArrowBack,
} from "@mui/icons-material";
import moment from "moment";
import DateRangePicker from "../../app/components/DateRangePicker";
import SubscriptionWaivers from "./SubscriptionWaivers";

export const VendorDetails = ({
  getVendorFinanceDetails,
  vendorFinance,
  isLoading,
}) => {
  const navigate = useNavigate();
  const { vendorId } = useParams();

  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);

  React.useEffect(() => {
    if (vendorId) {
      getVendorFinanceDetails({ vendorId });
    }
  }, [vendorId, getVendorFinanceDetails]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-EG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);

  const formatDateParam = (date) => date?.format?.("YYYY-MM-DD") ?? date;

  const handleFilter = () => {
    if (!vendorId) return;
    const query = { vendorId };
    if (startDate && endDate) {
      query.startDate = formatDateParam(startDate);
      query.endDate = formatDateParam(endDate);
    }
    getVendorFinanceDetails(query);
  };

  const vendor = vendorFinance?.vendor;
  const feesByType = vendorFinance?.feesByType || {};
  const totalCods = vendorFinance?.totalCods || 0;
  const netProfit = vendorFinance?.netProfit || 0;

  const [selectedFeeTypes, setSelectedFeeTypes] = React.useState([]);

  React.useEffect(() => {
    setSelectedFeeTypes(Object.keys(feesByType || {}));
  }, [feesByType]);

  const feeTypeLabels = {
    subscription: "Subscription",
    backtrack: "Backtrack",
    handling: "Handling",
    inbound: "Inbound",
    outbound: "Outbound",
    overstock: "Overstock",
    pickAndPack: "Pick & Pack",
    material: "Material",
    largeOrder: "Large Order",
    transportation: "Transportation",
    orderPickup: "Order Pickup",
    repacking: "Repacking",
    other: "Other",
  };

  const totalFeesByType = Object.values(feesByType).reduce(
    (sum, value) => sum + (value || 0),
    0
  );

  const selectedFeesTotal = Object.entries(feesByType).reduce(
    (sum, [type, value]) =>
      selectedFeeTypes.includes(type) ? sum + (value || 0) : sum,
    0
  );

  const handleToggleFeeType = (type) => {
    setSelectedFeeTypes((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  };

  const handleSelectAllFeeTypes = () => {
    setSelectedFeeTypes(Object.keys(feesByType || {}));
  };

  const handleClearFeeTypes = () => {
    setSelectedFeeTypes([]);
  };

  const hasRange = startDate && endDate;
  const rangeLabel = hasRange
    ? `${startDate.format("YYYY-MM-DD")} - ${endDate.format("YYYY-MM-DD")}`
    : "All time";

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper
        elevation={2}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                borderRadius: 2,
                backgroundColor: "var(--secColor)",
                color: "white",
              }}
            >
              <Store color="white" sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ color: "var(--secColor)", fontWeight: 700 }}>
                {vendor?.brandName || vendor?.name || "Vendor Details"}
              </Typography>
              {(vendor?.brandName && vendor?.name) && (
                <Typography variant="body2" color="text.secondary">
                  {vendor.name}
                </Typography>
              )}
              {vendor?.email && (
                <Typography variant="body2" color="text.secondary">
                  {vendor.email}
                </Typography>
              )}
            </Box>
          </Box>
          <MUIButton
            startIcon={<ArrowBack />}
            sx={{ textTransform: "none" }}
            onClick={() => navigate("/warehousing/finance/vendors")}
          >
            Back to Vendors
          </MUIButton>
        </Box>

        {vendorId && (
          <Box sx={{ mb: 3 }}>
            <SubscriptionWaivers vendorId={vendorId} />
          </Box>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} md={8}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}>
                Date Range (optional)
              </Typography>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={({ startDate: s, endDate: e }) => {
                  setStartDate(s || null);
                  setEndDate(e || null);
                }}
                fullWidth
                margin="dense"
                placeholder="Select date range"
              />
              <Typography variant="caption" color="text.secondary">
                Leave empty to view all-time performance.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: { xs: 2, md: 0 } }}>
                <MUIButton
                  sx={{ ...main_button, minWidth: 140 }}
                  onClick={handleFilter}
                  disabled={isLoading}
                >
                  Apply Filter
                </MUIButton>
              </Box>
              <Box sx={{ mt: 1, textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary">
                  Current range: {rangeLabel}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {isLoading && !vendorFinance && (
          <Box sx={{ ...centered_flex_box, minHeight: 200 }}>
            <Typography color="text.secondary">Loading vendor finance...</Typography>
          </Box>
        )}

        {vendorFinance && (
          <>
            <FoKpis
              items={[
                { label: "Total CODs", value: formatEgp(totalCods) + " EGP", note: "Cash in" },
                { label: "Total payouts", value: formatEgp(vendorFinance.totalPayouts) + " EGP" },
                { label: "Net profit", value: formatEgp(netProfit) + " EGP", hot: netProfit < 0 },
                { label: "Total fees", value: formatEgp(vendorFinance.totalFees) + " EGP" },
                { label: "Shipping fees", value: formatEgp(vendorFinance.totalShippingFees) + " EGP" },
                { label: "Compensations", value: formatEgp(vendorFinance.totalCompensations) + " EGP" },
                { label: "Recharges", value: formatEgp(vendorFinance.totalRecharges || 0) + " EGP" },
              ]}
            />

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: "rgba(0, 0, 0, 0.02)",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Fees by Type
              </Typography>
              {Object.keys(feesByType).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No fees for this vendor in the selected period.
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "space-between",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total fees (all types):{" "}
                      <strong>{formatCurrency(totalFeesByType)} EGP</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Selected fee types total:{" "}
                      <strong>{formatCurrency(selectedFeesTotal)} EGP</strong>
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <MUIButton
                        size="small"
                        sx={{ textTransform: "none" }}
                        onClick={handleSelectAllFeeTypes}
                      >
                        Select All
                      </MUIButton>
                      <MUIButton
                        size="small"
                        sx={{ textTransform: "none" }}
                        onClick={handleClearFeeTypes}
                      >
                        Clear
                      </MUIButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {Object.entries(feesByType)
                      .sort(([, a], [, b]) => (b || 0) - (a || 0))
                      .map(([type, value]) => {
                      const label = feeTypeLabels[type] || type;
                      const isSelected = selectedFeeTypes.includes(type);
                      return (
                        <Box
                          key={type}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            borderRadius: 1,
                            backgroundColor: isSelected
                              ? "rgba(76, 175, 80, 0.06)"
                              : "rgba(0, 0, 0, 0.02)",
                          }}
                        >
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ textTransform: "capitalize", fontWeight: 500 }}
                            >
                              {label}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, minWidth: 140, textAlign: "right" }}
                            >
                              {formatCurrency(value)} EGP
                            </Typography>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleToggleFeeType(type)}
                              sx={{ p: 0.5 }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}
            </Paper>
          </>
        )}
      </Paper>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  vendorFinance: state?.records?.vendorFinance,
  isLoading: state?.records?.isLoading,
});

const mapDispatchToProps = { getVendorFinanceDetails };

export default connect(mapStateToProps, mapDispatchToProps)(VendorDetails);

